import { StorageService } from '@/services/storage.service';

/**
 * Creates an SSE stream connection using fetch + ReadableStream.
 * Uses fetch instead of EventSource to support custom Authorization headers.
 *
 * @param {string} url - The SSE endpoint URL
 * @param {Object} body - The request body (sent as JSON POST)
 * @param {Object} callbacks - Event callbacks
 * @param {Function} [callbacks.onStatus] - Called on 'status' events: ({ step, document, progress })
 * @param {Function} [callbacks.onProgress] - Called on 'progress' events: ({ document_id, completed, total, field_count, field_filled_count, error })
 * @param {Function} [callbacks.onFieldResult] - Called on 'field_result' events: ({ fieldName, value, ... })
 * @param {Function} [callbacks.onComplete] - Called on 'complete' events: (payload)
 * @param {Function} [callbacks.onError] - Called on 'error' events or fetch failures: (error)
 * @returns {{ abort: Function }} Handle to abort the stream
 */
export function createSSEStream(url, body, callbacks = {}) {
  const controller = new AbortController();

  const processStream = async () => {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${StorageService.getToken()}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (callbacks.onError) callbacks.onError(err);
      return;
    }

    if (!response.ok) {
      if (callbacks.onError) {
        callbacks.onError(new Error(`SSE request failed with status ${response.status}`));
      }
      return;
    }

    if (!response.body) {
      if (callbacks.onError) callbacks.onError(new Error('SSE response has no body'));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = parseSSEBuffer(buffer);
        buffer = events.remaining;

        for (const event of events.parsed) {
          dispatchEvent(event, callbacks);
        }
      }

      // Flush remaining buffer — server may close without a trailing blank line
      if (buffer.trim()) {
        const finalEvents = parseSSEBuffer(buffer + '\n\n');
        for (const event of finalEvents.parsed) {
          dispatchEvent(event, callbacks);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (callbacks.onError) callbacks.onError(err);
    }
  };

  processStream();

  return {
    abort() {
      controller.abort();
    },
  };
}

/**
 * Parses SSE events from a text buffer.
 * Returns parsed events and any remaining incomplete data.
 */
function parseSSEBuffer(buffer) {
  const parsed = [];
  const normalized = buffer.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');
  const remaining = blocks.pop(); // Last block may be incomplete

  for (const block of blocks) {
    if (!block.trim()) continue;

    let eventType = 'message';
    let dataLines = [];

    const lines = block.split('\n');
    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    const data = dataLines.join('\n');

    if (data) {
      try {
        parsed.push({ type: eventType, data: JSON.parse(data) });
      } catch {
        parsed.push({ type: eventType, data });
      }
    }
  }

  return { parsed, remaining };
}

/**
 * Creates an SSE stream connection using fetch GET + ReadableStream.
 * Used for subscribing to extraction job events from ai-modules.
 *
 * @param {string} url - The SSE endpoint URL
 * @param {Object} callbacks - Event callbacks
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - External signal that aborts the stream
 * @returns {{ abort: Function }} Handle to abort the stream
 */
export function createGetSSEStream(url, callbacks = {}, { signal } = {}) {
  const controller = new AbortController();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const processStream = async () => {
    let response;
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${StorageService.getToken()}`,
        },
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (callbacks.onError) callbacks.onError(err);
      return;
    }

    if (!response.ok) {
      if (callbacks.onError) {
        callbacks.onError(new Error(`SSE request failed with status ${response.status}`));
      }
      return;
    }

    if (!response.body) {
      if (callbacks.onError) callbacks.onError(new Error('SSE response has no body'));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = parseSSEBuffer(buffer);
        buffer = events.remaining;

        for (const event of events.parsed) {
          dispatchEvent(event, callbacks);
        }
      }

      // Flush remaining buffer — server may close without a trailing blank line
      if (buffer.trim()) {
        const finalEvents = parseSSEBuffer(buffer + '\n\n');
        for (const event of finalEvents.parsed) {
          dispatchEvent(event, callbacks);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (callbacks.onError) callbacks.onError(err);
    }
  };

  processStream();

  return {
    abort() {
      controller.abort();
    },
  };
}

function dispatchEvent(event, callbacks) {
  switch (event.type) {
    case 'status':
      if (callbacks.onStatus) callbacks.onStatus(event.data);
      break;
    case 'progress':
      if (callbacks.onProgress) callbacks.onProgress(event.data);
      break;
    case 'field_result':
      if (callbacks.onFieldResult) callbacks.onFieldResult(event.data);
      break;
    case 'complete':
      if (callbacks.onComplete) callbacks.onComplete(event.data);
      break;
    case 'error':
      if (callbacks.onError) callbacks.onError(event.data);
      break;
    case 'queued':
      if (callbacks.onQueued) callbacks.onQueued(event.data);
      break;
    case 'cancelled':
      if (callbacks.onCancelled) callbacks.onCancelled(event.data);
      break;
    default:
      break;
  }
}
